<template>
  <ContentModeWrapper
    contentModeKey="nebenkostenabrechnung"
    :forceMode="inModal ? 'standard' : undefined"
  >
    <template #main="{ contentMode }">
      <div class="main-slot-content">
        <!-- CREATE / EDIT -->
        <h2 v-if="inModal" class="mt-12">
          {{
            selectedInvoiceId
              ? t('pages.operatingcostinvoices.headlineUtilitybill', { count: invoices.length })
              : t('pages.operatingcostinvoices.headlineUNewUtilitybill', { count: invoices.length })
          }}
        </h2>

        <ContentTabs data-key="cost-invoice-actual-document-tabs" :lazy="true" keepMounted>
          <ContentTab v-if="'standard' == (contentMode as ContentMode)" name="document">
            <template #header
              ><span v-tooltip="{ value: t('titletags.ociTabDocument'), showDelay: 1000 }">{{
                t('pages.operatingcostinvoices.contentTabs.ociTabDocument')
              }}</span></template
            >
            <template #content>
              <!-- CURRENT DOCUMENT IN STANDARD VIEW-->
              <SectionWrapper v-if="utilityBills?.length">
                <!-- <h3>
                  {{
                    t('pages.operatingcostinvoices.headlineUtilitybills', { count: utilityBills.length })
                  }}
                </h3> -->
                <div
                  v-for="(utilityBill, idx) in utilityBills"
                  :key="utilityBill.documentId || `bill-${idx}`"
                >
                  <SingleAccordion
                    v-if="utilityBill?.documentId"
                    :lazy="true"
                    :title="utilityBill.name"
                    :fileNameForIcon="utilityBill.name"
                  >
                    <FileViewer
                      :uuid="utilityBill.documentId"
                      :mime="utilityBill.mimeType ?? 'application/pdf'"
                      :filename="utilityBill.name"
                    />
                  </SingleAccordion>
                </div>
              </SectionWrapper>
            </template>
          </ContentTab>
          <ContentTab
            v-if="
              hasAiFeature &&
              aiSuggestions.length &&
              ((2 != activeStepValue &&
                'split' == (contentMode as ContentMode) &&
                !isStep1Successful) ||
                'standard' == (contentMode as ContentMode))
            "
            name="ocrdata"
          >
            <template #header
              ><i class="pi pi-sparkles text-purple-500"></i
              ><span v-tooltip="{ value: t('titletags.ociTabOcr'), showDelay: 1000 }">{{
                t('pages.operatingcostinvoices.contentTabs.ociOcr')
              }}</span></template
            >
            <template #content>
              <!-- <h3 class="pt-8">
                {{ t('pages.operatingcostinvoices.headlineAiSuggestions') }}
              </h3> -->

              <SectionWrapper v-if="aiSuggestions.length" css="ai-section">
                <NotificationBar
                  class="mb-4"
                  :mainText="t('pages.operatingcostinvoices.aiSuggestionsInfoText')"
                  severity="ai"
                  :invertStyle="true"
                  :showCloseButton="false"
                />

                <!-- 📋 MASTER DATA (Step 1 only) -->
                <OperatingCostInvoicesDetail
                  v-if="activeStepValue === 1 && isAiSourceActive && currentAiSuggestion"
                  :initialData="currentAiSuggestion"
                  :isNew="true"
                  :editMode="false"
                  :inModal="true"
                  :isSubComponent="isSubComponent"
                  :ticket="ticket"
                  :contracts="contractsProp"
                  templateSource="ai"
                  debugMountContext="main.ocrdata.step1.ai"
                />

                <!-- 📍 LINE ITEMS (Step 2 only - reactive to Stepper clicks) -->
                <div v-if="activeStepValue === 2 && currentAiSuggestion?.lineItems?.length">
                  <h3 class="pt-8 font-semibold">{{ t('text.invoiceLineItems') }}</h3>
                  <OperatingCostInvoiceLines
                    v-if="currentAiSuggestion.id"
                    :invoiceId="currentAiSuggestion.id"
                    :isNew="true"
                    :editMode="false"
                    :existingOperatingCostInvoiceLines="currentAiSuggestion.lineItems ?? []"
                    :onUpdateLineItemsOnCreate="() => {}"
                    :fetchData="async () => {}"
                    :currentContractId="currentContractId"
                    :selectedImportSourceInvoiceId="templateSourceInvoiceId"
                    :templateSource="templateSource"
                  />
                </div>
              </SectionWrapper>
            </template>
          </ContentTab>
        </ContentTabs>

        <!-- TODO: check what this is for (I cannot remember it's purpose)-->
        <!-- <ModalOrPage v-if="props.existingListMode === 'modal'" ref="showInvoiceRef">
          <template #default="{ state }">
            <OperatingCostInvoicesDetail
              :uuid="state?.uuid ?? undefined"
              :isNew="false"
              :editMode="false"
              :inModal="true"
              :ticket="ticket"
              :isSubComponent="isSubComponent"
              :contracts="contractsProp"
              :latestInvoiceId="latestInvoiceIdForDetail"
              :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
            />
          </template>
        </ModalOrPage> -->

        <!-- � STEPPER: Two-step invoice creation workflow (Basisdaten → Positionen) -->
        <!-- Active step determined by phase prop and data availability -->
        <Stepper v-model:value="activeStepValue" class="mb-4 mt-8" @update:value="onStepChange">
          <StepList v-if="isPanel2Accessible">
            <Step :value="1">{{ t('text.masterData') }}</Step>
            <Step :value="2" :disabled="!isPanel2Accessible">{{ t('text.invoiceLineItems') }}</Step>
          </StepList>
          <StepPanels>
            <!-- Panel 1: Basisdaten (Forms for invoice header) -->
            <StepPanel :value="1">
              <div>
                <ContentTabs data-key="cost-invoice-compare-main-tabs" :lazy="true" keepMounted>
                  <ContentTab name="actual">
                    <template #header
                      ><span v-tooltip="{ value: t('titletags.ociTabForm'), showDelay: 1000 }">{{
                        t('pages.operatingcostinvoices.contentTabs.ociTabForm')
                      }}</span></template
                    >
                    <template #content>
                      <NotificationBar
                        v-if="
                          currentContractId &&
                          !selectedInvoiceId &&
                          ((existingInvoicesForList.length > 0 &&
                            templateSourceInvoice &&
                            !isUsingAiAsTemplate) ||
                            isUsingAiAsTemplate)
                        "
                        class="mt-2"
                        :mainText="
                          isUsingAiAsTemplate
                            ? t('pages.operatingcostinvoices.aiSuggestionsSelectedTemplate')
                            : t(notificationKey, {
                                name: templateSourceInvoice?.issuerLegalName,
                              })
                        "
                        :severity="
                          isUsingAiAsTemplate
                            ? 'ai'
                            : !!isUsingLatestInvoiceAsTemplate
                              ? 'info'
                              : ''
                        "
                      />

                      <Toolbar v-if="currentContractId && !isStep1Successful">
                        <template #start>
                          <div class="pr-4">
                            <OciTemplateSourceSelect
                              :templateSourceInvoiceId="templateSourceInvoiceId"
                              :templateSourceOptions="templateSourceOptionsFormatted"
                              :selectedTemplateSourceOption="selectedTemplateSourceOption"
                              @update:templateSourceInvoiceId="templateSourceInvoiceId = $event"
                            />
                          </div>
                        </template>
                      </Toolbar>

                      <!-- NEW (Create) OR EXISTING (Phase 2 Edit) -->
                      <OperatingCostInvoicesDetail
                        :key="selectedInvoiceId ? 'existing-invoice' : 'new-invoice'"
                        :uuid="selectedInvoiceId ?? undefined"
                        :isNew="!selectedInvoiceId"
                        :editMode="effectiveEditMode && phase !== 'second'"
                        :inModal="true"
                        :isSubComponent="isSubComponent"
                        :initialData="!selectedInvoiceId ? initialDataForCreate : undefined"
                        :placeholders="
                          effectiveEditMode && phase !== 'second'
                            ? placeholdersForDetail
                            : undefined
                        "
                        :suggestions="
                          effectiveEditMode && phase !== 'second' ? suggestions : undefined
                        "
                        :ticket="ticket"
                        :allDisabled="false"
                        :disabledFields="
                          effectiveEditMode && phase !== 'second'
                            ? disabledFieldsForDetail
                            : undefined
                        "
                        :contracts="contractsProp"
                        :latestInvoiceId="latestInvoiceIdForDetail"
                        :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
                        :importSourceInvoices="importSourceInvoices"
                        :templateSource="
                          effectiveEditMode && phase !== 'second' ? templateSource : undefined
                        "
                        :allowOperatingCostInvoiceLines="false"
                        :selectedImportSourceInvoiceId="templateSourceInvoiceId"
                        debugMountContext="main.stepper.panel1.actual"
                        @submitSuccess="onCreated"
                        @contractIdChange="onContractSelected"
                        @invoiceLoaded="onInvoiceLoaded"
                      />
                    </template>
                  </ContentTab>

                  <ContentTab
                    v-if="
                      'standard' == (contentMode as ContentMode) &&
                      currentContractId &&
                      phase !== 'second'
                    "
                    name="selection"
                  >
                    <template #header>
                      <div class="flex items-center gap-2">
                        <i
                          v-if="templateSourceInvoiceId"
                          :class="
                            getSourceTypeIconClass(
                              selectedTemplateSourceOption?.sourceType ?? 'other',
                            )
                          "
                        />
                        <span v-tooltip="{ value: dynamicCompareTabTooltip, showDelay: 1000 }">
                          {{ dynamicCompareTabLabel }}
                        </span>
                      </div>
                    </template>
                    <template #content>
                      <NotificationBar
                        v-if="
                          currentContractId &&
                          !selectedInvoiceId &&
                          ((existingInvoicesForList.length > 0 &&
                            templateSourceInvoice &&
                            !isUsingAiAsTemplate) ||
                            isUsingAiAsTemplate)
                        "
                        class="mt-2"
                        :invertStyle="true"
                        :mainText="
                          isUsingAiAsTemplate
                            ? t('pages.operatingcostinvoices.aiSuggestionsSelectedTemplateAlt')
                            : t(`${notificationKey}Alt`, {
                                name: templateSourceInvoice?.issuerLegalName,
                              })
                        "
                        :severity="
                          isUsingAiAsTemplate
                            ? 'ai'
                            : !!isUsingLatestInvoiceAsTemplate
                              ? 'info'
                              : ''
                        "
                      />

                      <Toolbar v-if="currentContractId">
                        <template #start>
                          <div class="pr-4">
                            <OciTemplateSourceSelect
                              :templateSourceInvoiceId="templateSourceInvoiceId"
                              :templateSourceOptions="templateSourceOptionsFormatted"
                              :selectedTemplateSourceOption="selectedTemplateSourceOption"
                              @update:templateSourceInvoiceId="templateSourceInvoiceId = $event"
                            />
                          </div>
                        </template>
                      </Toolbar>

                      <!-- v-if="!!templateSourceInvoice" -->
                      <OperatingCostInvoicesDetail
                        v-if="templateSourceInvoice"
                        :initialData="templateSourceInvoice"
                        :isNew="true"
                        :editMode="false"
                        :inModal="true"
                        :isSubComponent="isSubComponent"
                        :ticket="ticket"
                        :contracts="contractsProp"
                        :templateSource="templateSource"
                        debugMountContext="main.stepper.panel1.selection"
                      />
                    </template>
                  </ContentTab>
                  <ContentTab
                    v-if="
                      'standard' == (contentMode as ContentMode) &&
                      !!templateSourceInvoice &&
                      invoices.length &&
                      wrapperIsNew
                    "
                    name="templates"
                  >
                    <template #header
                      ><i class="pi pi-receipt"></i
                      ><span
                        v-tooltip="{ value: t('titletags.ociExistingOCR'), showDelay: 1000 }"
                        >{{ t('pages.operatingcostinvoices.contentTabs.ociExistingOCR') }}</span
                      ></template
                    >

                    <template #content>
                      <h3 class="pt-8">
                        {{ t('pages.operatingcostinvoices.headlineExistingInvoices') }}
                      </h3>
                      <SectionWrapper v-if="invoices.length && wrapperIsNew" class="mt-2">
                        <DataTable
                          data-key="operatingcostinvoice"
                          :data="existingInvoicesForList"
                          :columns="columns"
                          :isLoadingFinished="isLoadingFinished"
                          selectionMode="single"
                          :selectedRows="selectedExistingInvoiceRows"
                          :checkboxMultiselect="false"
                          :tableActions="false"
                          :showPrimaryToolbar="false"
                          :showSecondaryToolbarButtons="false"
                          :showSecondaryToolbar="false"
                          :hasExpandedRow="true"
                          :onRowClick="onRowClick"
                          @update:selectedRows="selectedExistingInvoiceRows = $event"
                        >
                          <template #custom-expanded-row="{ row }">
                            <OperatingCostInvoicesDetail
                              v-if="row.id?.meta('rawvalue')"
                              :uuid="row.id?.meta('rawvalue')"
                              :isNew="false"
                              :editMode="false"
                              :inModal="true"
                              :isSubComponent="isSubComponent"
                              :ticket="ticket"
                              :contracts="contractsProp"
                              :latestInvoiceId="latestInvoiceIdForDetail"
                              :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
                              :templateSource="
                                row.id?.meta('rawvalue') === latestInvoice?.id
                                  ? 'latest'
                                  : undefined
                              "
                              :debugMountContext="`main.stepper.panel1.templates.expanded.${row.id?.meta('rawvalue') ?? 'unknown'}`"
                            />
                          </template>
                        </DataTable>
                      </SectionWrapper>
                    </template>
                  </ContentTab>
                </ContentTabs>
              </div>
            </StepPanel>

            <!-- Panel 2: Positionen (Line items table) -->
            <StepPanel :value="2">
              <NotificationBar
                v-if="
                  currentContractId &&
                  ((existingInvoicesForList.length > 0 &&
                    templateSourceInvoice &&
                    !isUsingAiAsTemplate) ||
                    isUsingAiAsTemplate)
                "
                class="mt-2"
                :mainText="
                  isUsingAiAsTemplate
                    ? t('pages.operatingcostinvoices.aiSuggestionsSelectedTemplate')
                    : t(notificationKey, {
                        name: templateSourceInvoice?.issuerLegalName,
                      })
                "
                :severity="
                  isUsingAiAsTemplate ? 'ai' : !!isUsingLatestInvoiceAsTemplate ? 'info' : ''
                "
              />

              <OperatingCostInvoiceLines
                v-if="selectedInvoiceId"
                :invoiceId="selectedInvoiceId"
                :isNew="false"
                :editMode="effectiveEditMode"
                :existingOperatingCostInvoiceLines="editDataLineItems"
                :onUpdateLineItemsOnCreate="(data) => onLineItemsCreated({ ok: true })"
                :fetchData="fetchLineItems"
                :latestInvoiceId="latestInvoiceIdForDetail"
                :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
                :importSourceInvoices="importSourceInvoices"
                :invoiceSourceMap="invoiceSourceMap"
                :currentContractId="currentInvoiceContractId"
                :selectedImportSourceInvoiceId="templateSourceInvoiceId"
                :templateSource="templateSource"
              >
                <template #templateSourceSelector>
                  <!-- 🎯 Template Source Selector in Phase 2 -->
                  <!-- <label class="text-sm font-semibold text-gray-700 block mb-2">
                      {{ t('pages.operatingcostinvoices.templateSourceLabel') }}
                    </label> -->
                  <div class="pr-4">
                    <OciTemplateSourceSelect
                      :templateSourceInvoiceId="templateSourceInvoiceId"
                      :templateSourceOptions="templateSourceOptionsFormatted"
                      :selectedTemplateSourceOption="selectedTemplateSourceOption"
                      @update:templateSourceInvoiceId="templateSourceInvoiceId = $event"
                    />
                  </div>
                </template>
              </OperatingCostInvoiceLines>
            </StepPanel>
          </StepPanels>
        </Stepper>
      </div>
    </template>

    <!-- 📑 SIDECAR SLOT: Secondary content (shown in split and external window mode) -->
    <template #sidecar>
      <ContentTabs data-key="cost-invoice-actual-document-sidecar-tabs" :lazy="true" keepMounted>
        <ContentTab name="document">
          <template #header
            ><span v-tooltip="{ value: t('titletags.ociTabDocument'), showDelay: 1000 }">{{
              t('pages.operatingcostinvoices.contentTabs.ociTabDocument')
            }}</span></template
          >
          <template #content>
            <!-- CURRENT DOCUMENT IN STANDARD VIEW-->
            <SectionWrapper v-if="utilityBills?.length">
              <div
                v-for="(utilityBill, idx) in utilityBills"
                :key="utilityBill.documentId || `bill-${idx}`"
              >
                <FileViewer
                  v-if="utilityBill?.documentId"
                  :uuid="utilityBill.documentId"
                  :mime="utilityBill.mimeType ?? 'application/pdf'"
                  :filename="utilityBill.name"
                />
              </div>
            </SectionWrapper>
          </template>
        </ContentTab>
        <ContentTab v-if="hasAiFeature && aiSuggestions.length && isStep1Successful" name="ocrdata">
          <template #header
            ><i class="pi pi-sparkles text-purple-500"></i
            ><span v-tooltip="{ value: t('titletags.ociTabOcr'), showDelay: 1000 }">{{
              t('pages.operatingcostinvoices.contentTabs.ociOcr')
            }}</span></template
          >
          <template #content>
            <SectionWrapper v-if="aiSuggestions.length" css="ai-section">
              <NotificationBar
                class="mb-4"
                :mainText="t('pages.operatingcostinvoices.aiSuggestionsInfoText')"
                severity="ai"
                :invertStyle="true"
                :showCloseButton="false"
              />

              <!-- 📋 MASTER DATA (Step 1 only) -->
              <OperatingCostInvoicesDetail
                v-if="syncedActiveStepValue === 1 && isAiSourceActive && currentAiSuggestion"
                :initialData="currentAiSuggestion"
                :isNew="true"
                :editMode="false"
                :inModal="true"
                :isSubComponent="isSubComponent"
                :ticket="ticket"
                :contracts="contractsProp"
                templateSource="ai"
                debugMountContext="sidecar.ocrdata.step1.ai"
              />

              <!-- 📍 LINE ITEMS (Step 2 only - reactive to Stepper clicks) -->
              <div v-if="syncedActiveStepValue === 2 && currentAiSuggestion?.lineItems?.length">
                <h3 class="pt-8 font-semibold">{{ t('text.invoiceLineItems') }}</h3>
                <OperatingCostInvoiceLines
                  v-if="currentAiSuggestion.id"
                  :invoiceId="currentAiSuggestion.id"
                  :isNew="true"
                  :editMode="false"
                  :existingOperatingCostInvoiceLines="currentAiSuggestion.lineItems ?? []"
                  :onUpdateLineItemsOnCreate="() => {}"
                  :fetchData="async () => {}"
                  :currentContractId="currentContractId"
                  :selectedImportSourceInvoiceId="templateSourceInvoiceId"
                  :forceInvoiceItemSource="'ai'"
                  :showSuggestionRowStyle="false"
                  :checkboxMultiselect="false"
                  :disableRowModal="true"
                  :templateSource="'ai'"
                />
              </div>
            </SectionWrapper>
          </template>
        </ContentTab>
      </ContentTabs>

      <!-- � STEPPER: Two-step invoice creation workflow (Basisdaten → Positionen) -->
      <!-- Active step determined by phase prop and data availability -->

      <div>
        <ContentTabs data-key="cost-invoice-compare-sidecar-tabs" :lazy="true" keepMounted>
          <ContentTab v-if="syncedCurrentContractId && phase !== 'second'" name="selection">
            <template #header>
              <div class="flex items-center gap-2">
                <i
                  v-if="syncedTemplateSourceId"
                  :class="
                    getSourceTypeIconClass(
                      syncedSelectedTemplateSourceOption?.sourceType ?? 'other',
                    )
                  "
                />
                <span v-tooltip="{ value: dynamicCompareTabTooltipForSidecar, showDelay: 1000 }">
                  {{ dynamicCompareTabLabelForSidecar }}
                </span>
              </div>
            </template>
            <template #content>
              <NotificationBar
                v-if="
                  syncedCurrentContractId &&
                  !selectedInvoiceId &&
                  (syncedSelectedTemplateSourceOption || isUsingAiAsTemplateFromSync)
                "
                class="mt-2"
                :invertStyle="true"
                :mainText="
                  isUsingAiAsTemplateFromSync
                    ? t('pages.operatingcostinvoices.aiSuggestionsSelectedTemplateAlt')
                    : t(
                        getTemplateNotificationKey(
                          syncedSelectedTemplateSourceOption?.sourceType === 'latest',
                        ) + 'Alt',
                        {
                          name: syncedTemplateSourceInvoice?.issuerLegalName,
                        },
                      )
                "
                :severity="
                  isUsingAiAsTemplateFromSync
                    ? 'ai'
                    : syncedSelectedTemplateSourceOption?.sourceType === 'latest'
                      ? 'info'
                      : ''
                "
              />

              <OperatingCostInvoicesDetail
                v-if="syncedTemplateSourceInvoice"
                :initialData="syncedTemplateSourceInvoice"
                :isNew="true"
                :editMode="false"
                :inModal="true"
                :isSubComponent="isSubComponent"
                :ticket="ticket"
                :contracts="contractsProp"
                :templateSource="templateSource"
                debugMountContext="sidecar.compare.selection"
              />
            </template>
          </ContentTab>
          <ContentTab
            v-if="!!syncedTemplateSourceInvoice && invoices.length && wrapperIsNew"
            name="templates"
          >
            <template #header
              ><i class="pi pi-receipt"></i
              ><span v-tooltip="{ value: t('titletags.ociExistingOCR'), showDelay: 1000 }">{{
                t('pages.operatingcostinvoices.contentTabs.ociExistingOCR')
              }}</span></template
            >

            <template #content>
              <h3 class="pt-8">
                {{ t('pages.operatingcostinvoices.headlineExistingInvoices') }}
              </h3>
              <SectionWrapper v-if="invoices.length && wrapperIsNew" class="mt-2">
                <DataTable
                  data-key="operatingcostinvoice"
                  :data="existingInvoicesForList"
                  :columns="columns"
                  :isLoadingFinished="isLoadingFinished"
                  selectionMode="single"
                  :selectedRows="selectedExistingInvoiceRows"
                  :checkboxMultiselect="false"
                  :tableActions="false"
                  :showPrimaryToolbar="false"
                  :showSecondaryToolbarButtons="false"
                  :showSecondaryToolbar="false"
                  :hasExpandedRow="true"
                  :onRowClick="onRowClick"
                  @update:selectedRows="selectedExistingInvoiceRows = $event"
                >
                  <template #custom-expanded-row="{ row }">
                    <OperatingCostInvoicesDetail
                      v-if="row.id?.meta('rawvalue')"
                      :uuid="row.id?.meta('rawvalue')"
                      :isNew="false"
                      :editMode="false"
                      :inModal="true"
                      :isSubComponent="isSubComponent"
                      :ticket="ticket"
                      :contracts="contractsProp"
                      :latestInvoiceId="latestInvoiceIdForDetail"
                      :latestInvoiceLineItems="latestInvoiceLineItemsForDetail"
                      :templateSource="
                        row.id?.meta('rawvalue') === latestInvoice?.id ? 'latest' : undefined
                      "
                      :debugMountContext="`sidecar.compare.templates.expanded.${row.id?.meta('rawvalue') ?? 'unknown'}`"
                    />
                  </template>
                </DataTable>
              </SectionWrapper>
            </template>
          </ContentTab>
        </ContentTabs>
      </div>
    </template>
  </ContentModeWrapper>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, toRaw, watch } from 'vue';
import type { DataTableRowClickEvent } from 'primevue/datatable';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import ContentModeWrapper from '@/components/contentmode/ContentModeWrapper.vue';
import type { ModalOrPageExpose } from '@/components/details/detail-modal-or-page/DetailModalOrPage.vue';
import { buildPlaceholders } from '@/components/details/detailTypes';
import FileViewer from '@/components/file-viewer/FileViewer.vue';
import SectionWrapper from '@/components/section-wrapper/SectionWrapper.vue';
import type { ContractSimple } from '@/views/contracts/contractsTypes';
import { useContracts, useContractsConverter } from '@/views/contracts/hooks/useContracts';
import { useOperatingCostInvoiceLinesConverter } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/hooks/useOperatingCostInvoiceLines';
import OperatingCostInvoiceLines from '@/views/operatingcostinvoices/components/operationcostinvoicelines/OperatingCostInvoiceLines.vue';
import type { OperatingCostInvoiceLineItemSimple } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import type {
  InvoiceItemSource,
  InvoiceItemSuggestionSource,
} from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import { useAiSuggestions } from '@/views/operatingcostinvoices/composables/useAiSuggestions';
import { columns } from '@/views/operatingcostinvoices/configs/operatingCostInvoiceColumnConfig';
import {
  useOperatingCostInvoices,
  useOperatingCostInvoicesConverter,
} from '@/views/operatingcostinvoices/hooks/useOperatingCostInvoices';
import OperatingCostInvoicesDetail from '@/views/operatingcostinvoices/OperatingcostinvoicesDetail.vue';
import type { OperatingCostInvoiceSimple } from '@/views/operatingcostinvoices/operatingCostInvoiceTypes';
import { getOperatingCostDocumentInternalId } from '@/views/operatingcostinvoices/utils/ticketDocuments';
import { buildFieldSuggestions } from '@/composables/useFieldSuggestions';
import { useStateSync } from '@/composables/useStateSync';
import { useStateSyncNotifications } from '@/composables/useStateSyncNotifications';
import type { ContentMode } from '@/stores/contentmode';
import { getNextPeriod, getSameDayNextYear } from '@/utils/dateUtils';
import { type ContractV1Dto, OperatingCostInvoiceV1Dto, TicketDocumentV1Dto } from '@/openapi';
import { eventBus } from '@/configs/eventBus';

import type { CostInvoicePhase } from '../processes/injectables/uiPlugins/costStatementTypes';
import type { TicketEnriched } from '../tickets/ticketsTypes';

const props = withDefaults(
  defineProps<{
    contractId?: string;
    invoiceId?: string | null;
    step1Successful?: boolean;
    ticket?: TicketEnriched | null;
    isNew?: boolean;
    editMode?: boolean;
    inModal?: boolean;
    isSubComponent?: boolean;
    existingListMode?: 'inline' | 'modal';
    phase?: CostInvoicePhase;
    hasAiFeature?: boolean;
    allowOperatingCostInvoiceLines?: boolean;

    // utilityBills from ticket context (to prefill certain fields / suggestions)
    utilityBills?: TicketDocumentV1Dto[];

    // prefills/placeholders/flags overrides
    initialDataOverride?: Partial<OperatingCostInvoiceSimple>;
    placeholdersOverride?: Partial<Record<keyof OperatingCostInvoiceSimple, string>>;
    latestInvoiceIdOverride?: string | null;
    latestInvoiceLineItemsOverride?: OperatingCostInvoiceLineItemSimple[];
    disabledFieldsOverride?: Partial<Record<keyof OperatingCostInvoiceSimple, boolean>>;
  }>(),
  {
    contractId: undefined,
    invoiceId: null,
    step1Successful: undefined,
    ticket: null,
    isNew: undefined,
    editMode: undefined,
    inModal: false,
    isSubComponent: false,
    existingListMode: 'inline',
    phase: 'both',
    hasAiFeature: true,
    allowOperatingCostInvoiceLines: undefined,

    utilityBills: undefined,
    initialDataOverride: undefined,
    placeholdersOverride: undefined,
    latestInvoiceIdOverride: undefined,
    latestInvoiceLineItemsOverride: undefined,
    disabledFieldsOverride: undefined,
  },
);

const emit = defineEmits<{
  (e: 'created', payload: { id?: string }): void;
  (e: 'edited', payload: { id?: string }): void;
  (e: 'contractIdChange', contractId: string | undefined): void;
  (e: 'lineItemsCreated', payload: { ok: boolean }): void;
}>();

const { t, locale } = useI18n();

const route = useRoute();

// 🔄 State Sync Adapter (Local + External Window)
const { syncState } = useStateSync('nebenkostenabrechnung', 'mainPanel'); // Main Panel sender

const { fetchContract } = useContracts();
const { convertContractDtoToContractSimple } = useContractsConverter();

const { fetchOperatingCostInvoice } = useOperatingCostInvoices();
const {
  convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList,
  fetchAllAndConvertToSimple,
} = useOperatingCostInvoicesConverter();
const { convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList } =
  useOperatingCostInvoiceLinesConverter();
const { getAiSuggestionsForDocument } = useAiSuggestions();
// Quick local switch: const { getAiSuggestionsForDocument } = useAiSuggestions({ mode: 'mock' });

const isLoadingFinished = ref(false);
const showInvoiceRef = ref<ModalOrPageExpose | null>(null);
const existingInvoices = ref<OperatingCostInvoiceSimple[]>([]);

// importSourceInvoices = existing invoices + AI suggestions (merged)
// This way LineItems watcher can find both without needing separate logic
const importSourceInvoices = computed<OperatingCostInvoiceSimple[]>(() => [
  ...existingInvoices.value,
  ...aiSuggestions.value,
]);

/**
 * 🎯 Map Invoice ID → Source Type
 * Helft bei der Bestimmung: Ist diese Rechnung eine AI, Latest, oder Other?
 * Wird verwendet in OperatingCostInvoiceLines um korrekt zu mergen
 */
const invoiceSourceMap = computed<Record<string, InvoiceItemSource>>(() => {
  const map: Record<string, InvoiceItemSource> = {};

  // Mark AI suggestions
  aiSuggestions.value.forEach((inv) => {
    map[inv.id] = 'ai';
  });

  // Mark latest invoice
  if (latestInvoice.value?.id) {
    map[latestInvoice.value.id] = 'latest';
  }

  // Mark other existing invoices as 'other'
  existingInvoices.value.forEach((inv) => {
    if (!map[inv.id]) {
      map[inv.id] = 'other';
    }
  });

  return map;
});

// ===== STEPPER STATE =====
// CRITICAL: PrimeVue Stepper uses v-model:value (not v-model:activeStep)
const activeStepValue = ref<number>(1); // Main Step

// --- Step Sync: Main → Sidecar ---
const syncedActiveStepValue = ref<number>(1); // Sidecar Step

// Main: Sende Step-Wechsel an Sidecar
watch(
  activeStepValue,
  (val) => {
    // Keep local sidecar state aligned immediately (same-instance split view)
    syncedActiveStepValue.value = val;
    // Broadcast for external window / other listeners
    syncState('activeStepValue', val);
  },
  { immediate: true },
);

onMounted(() => {
  // Re-broadcast after mount so late listeners get the initial step.
  void nextTick(() => {
    syncState('activeStepValue', activeStepValue.value);
  });
});

// Sidecar: Empfange Step-Wechsel

useStateSyncNotifications({
  context: 'sidecar',
  onStateChange: (payload) => {
    if (payload.key === 'activeStepValue') {
      syncedActiveStepValue.value = payload.value;
    }
  },
});

// ----- AI SUGGESTIONS STATE --------------------------------------------------

const aiSuggestions = ref<OperatingCostInvoiceSimple[]>([]);
const isAiSourceActive = ref<boolean>(false); // Will be set to true if AI suggestions are available
const selectedExistingInvoiceRows = ref<any[]>([]); // Track selected rows for Existing Invoices
const selectedAiInvoiceRows = ref<any[]>([]); // Track selected rows for AI Suggestions

// ----- CONTRACT DATA ---------------------------------------------------------

const contractDto = ref<ContractV1Dto | undefined>(undefined);
const contractSimple = ref<ContractSimple | undefined>(undefined);

const selectedContractIdFromDetail = ref<string | undefined>(props.contractId ?? undefined);
const currentContractId = computed(() => {
  // Priority:
  // 1. passed from outside via prop
  // 2. else: selected in detail (e.g. when user changes contract in detail view)
  return props.contractId ?? selectedContractIdFromDetail.value ?? undefined;
});

const isUsingLatestInvoiceAsTemplate = computed(() => {
  const templateId = templateSourceInvoice.value?.id;
  const latestId = latestInvoice.value?.id;
  return !!templateId && !!latestId && templateId === latestId;
});

// Extract first "Nebenkostenabrechnung" document ID from ticket
const ticketNebenkostenAbrechungDocumentId = computed(() => {
  return getOperatingCostDocumentInternalId(props.ticket?.documents);
});

const isUsingAiAsTemplate = computed(() => {
  if (!templateSourceInvoiceId.value) return false;
  // Check if the current template is from AI suggestions
  return aiSuggestions.value.some((ai) => ai.id === templateSourceInvoiceId.value);
});

/** Determine initial active step based on phase prop */
const _initialActiveStep = computed<number>(() => {
  switch (props.phase) {
    case 'first':
      return 1; // Only show Panel 1
    case 'second':
      return 2; // Start on Panel 2 (editing mode)
    case 'both':
    default:
      return 1; // Start on Panel 1, progress to Panel 2
  }
});

/**
 * Check if Panel 2 can be activated
 * Basisdaten must be present (selectedInvoiceId is set after successful Panel 1 submission)
 * In 'second' mode, Panel 2 is always accessible (we're already editing)
 */
const isPanel2Accessible = computed<boolean>(() => {
  // In 'first' mode, only panel 1 is part of this step
  if (props.phase === 'first') {
    return false;
  }

  // In 'second' mode, Panel 2 is always accessible
  if (props.phase === 'second') {
    return true;
  }

  // In 'both' and 'first' modes, Panel 2 is only accessible after Panel 1 is saved
  return isStep1Successful.value && !!selectedInvoiceId.value;
});

/** Helper: Get translation key based on whether using latest invoice as template */
const getTemplateNotificationKey = (isUsingLatest: boolean): string =>
  isUsingLatest
    ? 'pages.operatingcostinvoices.latestUtilitybillExampleValues'
    : 'pages.operatingcostinvoices.otherUtilitybillExampleValues';

const notificationKey = computed(() =>
  getTemplateNotificationKey(isUsingLatestInvoiceAsTemplate.value),
);

function onContractSelected(id: string | null | undefined) {
  selectedContractIdFromDetail.value = id ?? undefined;
  emit('contractIdChange', id ?? undefined);
}

async function loadContract(contractId?: string) {
  if (!contractId) {
    contractDto.value = undefined;
    contractSimple.value = undefined;
    isLoadingFinished.value = true;
    return;
  }

  isLoadingFinished.value = false;
  try {
    const dto = await fetchContract(contractId);
    contractDto.value = dto;
    contractSimple.value = convertContractDtoToContractSimple(dto);
  } finally {
    isLoadingFinished.value = true;
  }
}

// ----- EXISTING LIST DATA (without currently selected invoice) ----------------

const existingInvoicesForList = computed<OperatingCostInvoiceSimple[]>(() => {
  if (!invoices.value?.length) return [];

  // if an invoice is currently selected, exclude it from the existing list
  if (selectedInvoiceId.value) {
    return invoices.value.filter((inv) => inv.id !== selectedInvoiceId.value);
  }

  return invoices.value;
});

watch(
  currentContractId,
  (val) => {
    void loadContract(val ?? undefined);
  },
  { immediate: true },
);

onMounted(async () => {
  // Auto-detect which desktop slot context we're in
  // - If rendered in 'split' named router view → 'main-panel' (left side)
  // - If rendered in 'default' named router view → 'split-panel' (right side)
  const lastMatchedRoute = route.matched[route.matched.length - 1];
  const _currentRouteName = typeof lastMatchedRoute?.name === 'string' ? lastMatchedRoute.name : '';

  // Fetch all existing invoices
  existingInvoices.value = await fetchAllAndConvertToSimple();

  // 🐛 Debug: Log ALL state sync events
  eventBus.on('stateSync', (payload: any) => {
    // console.log(`[🐛 EventBus] Received stateSync event:`, payload);
    addDebugMessage(payload.key, payload.value, 'EventBus');
  });
});

// Convert DTOs from contract to simple list used in the table / logic
const invoices = computed<OperatingCostInvoiceSimple[]>(() =>
  convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList(
    contractDto.value?.operatingCostInvoices ?? [],
  ),
);

// Update existingInvoices when contract changes
watch(invoices, (newInvoices) => {
  existingInvoices.value = newInvoices;
});

// 🎯 Ensure activeStepValue follows phase changes (IMMEDIATELY on mount too!)
watch(
  () => props.phase,
  (newPhase) => {
    const correctStep = newPhase === 'second' ? 2 : 1;
    activeStepValue.value = correctStep;
  },
  { immediate: true }, // ← CRITICAL: trigger on first mount!
);

// Wrapper so OperatingCostInvoicesDetail can use its "contracts via props" branch
const contractsProp = computed<ContractV1Dto[]>(() => {
  if (contractDto.value) {
    return [contractDto.value];
  }

  if (!contractSimple.value) return [];

  return [
    {
      id: contractSimple.value.id,
      name: contractSimple.value.name,
    } as ContractV1Dto,
  ];
});

// ----- STATE FOR DETAIL ------------------------------------------------------

const selectedInvoiceId = ref<string | undefined>(props.invoiceId ?? undefined);
const currentInvoiceContractId = ref<string | undefined>(undefined);
const editDataLineItems = ref<OperatingCostInvoiceLineItemSimple[]>([]);
const latestInvoice = ref<OperatingCostInvoiceSimple | undefined>(undefined);
const latestInvoiceLineItems = ref<OperatingCostInvoiceLineItemSimple[]>([]);
const autoPlaceholders = ref<Partial<Record<keyof OperatingCostInvoiceSimple, string>>>({});
const latestValues = ref<Partial<OperatingCostInvoiceSimple>>({});

// Which invoice is currently used as template source for the NEW invoice
const templateSourceInvoiceId = ref<string | undefined>(undefined);

// ----- SIDECAR SYNC STATE (Main Panel receives updates from Sidecar) -------

const syncedCurrentContractId = ref<string | undefined>(undefined);
const syncedTemplateSourceId = ref<string | undefined>(undefined);
const syncedTemplateSourceInvoice = ref<OperatingCostInvoiceSimple | undefined>(undefined);
const syncedLatestInvoiceId = ref<string | undefined>(undefined); // ← NEW: Latest invoice ID from Main Panel
const isUsingAiAsTemplateFromSync = ref<boolean>(false);
const sidecarTemplateSourceOptions = ref<
  Array<{ label: string; value: string; sourceType: InvoiceItemSuggestionSource }>
>([]);
const debugSidecarMessages = ref<
  Array<{ timestamp: string; key: string; value: any; type: string }>
>([]);

// (Debug watcher for templateSourceInvoiceId removed)

const templateSourceInvoice = computed<OperatingCostInvoiceSimple | undefined>(() => {
  if (templateSourceInvoiceId.value) {
    // 1️⃣ Look in existing invoices first
    const match = invoices.value.find((inv) => inv.id === templateSourceInvoiceId.value);
    if (match) return match;

    // 2️⃣ Then look in AI suggestions
    const matchAi = aiSuggestions.value.find((ai) => ai.id === templateSourceInvoiceId.value);
    if (matchAi) return matchAi;
  }
  // 3️⃣ Fallback: use latest invoice as template
  if (latestInvoice.value) return latestInvoice.value;

  // 4️⃣ Fallback: use first AI suggestion if available (ensures comparison tab displays)
  if (aiSuggestions.value.length > 0) return aiSuggestions.value[0];

  return undefined;
});

// Remove computed selectedImportSourceInvoiceIdForDetail - no longer needed
// LineItems component will validate the ID internally

// Extract line items from AI suggestions when AI is used as template
const _aiSuggestionsLineItems = computed<OperatingCostInvoiceLineItemSimple[]>(() => {
  // Return AI line items whenever they're available (not just when selected as template)
  if (currentAiSuggestion.value?.lineItems) {
    return currentAiSuggestion.value.lineItems;
  }
  return [];
});

/**
 * 🤖 Aktuelle AI-Suggestion für die OCR-Tab
 * Returns die aktuell selektierte oder erste AI-Suggestion
 * Dies ist die richtige Quelle für initialData in der KI-Datenextrakt Tab
 */
const currentAiSuggestion = computed<OperatingCostInvoiceSimple | undefined>(() => {
  if (!isAiSourceActive.value || aiSuggestions.value.length === 0) {
    return undefined;
  }

  // When an AI suggestion is active (selected via templateSourceInvoiceId)
  if (isUsingAiAsTemplate.value && templateSourceInvoice.value) {
    return templateSourceInvoice.value;
  }

  // Fallback: erste AI-Suggestion
  return aiSuggestions.value[0] ?? undefined;
});

// 📡 SYNC isUsingAiAsTemplate to Sidecar/ExternalWindow
watch(
  () => isUsingAiAsTemplate.value,
  (isAi) => {
    syncState('isUsingAiAsTemplate', isAi);
  },
);

/**
 * 📄 Current Template Document für PDF Viewer im Sidecar
 * Extrahiert documentId, mimeType, und name aus dem aktuellen Datensatz
 *
 * Nutzt syncedTemplateSourceInvoice wenn vorhanden (vom Event erhalten)
 * Fallback auf lokale invoices/aiSuggestions Array wenn ID gespeichert ist
 */
const currentInvoiceForDebug = computed(() => {
  const timestamp = new Date().toLocaleTimeString();

  // 1️⃣ PREFER: Use synced invoice object (from event payload, always available)
  if (syncedTemplateSourceInvoice.value) {
    // console.log(
    //   `[${timestamp}] [Sidecar Debug] Using syncedTemplateSourceInvoice: ${syncedTemplateSourceInvoice.value.issuerLegalName}`,
    // );
    return syncedTemplateSourceInvoice.value;
  }

  // 2️⃣ FALLBACK: Search local arrays if only ID was synced
  const idToUse = syncedTemplateSourceId.value || templateSourceInvoiceId.value;

  if (!idToUse) {
    console.log(`[${timestamp}] [Sidecar Debug] No ID available`);
    return undefined;
  }

  console.log(
    `[${timestamp}] [Sidecar Debug] Looking for invoice with ID: ${idToUse} (fallback mode)`,
  );
  console.log(`[${timestamp}] [Sidecar Debug] Available invoices COUNT: ${invoices.value.length}`);
  console.log(
    `[${timestamp}] [Sidecar Debug] Available AI suggestions COUNT: ${aiSuggestions.value.length}`,
  );

  const invoice =
    invoices.value.find((inv) => inv.id === idToUse) ||
    aiSuggestions.value.find((inv) => inv.id === idToUse);

  if (invoice) {
    console.log(`[${timestamp}] [Sidecar Debug] ✅ Found invoice:`, {
      id: invoice.id,
      name: invoice.issuerLegalName,
    });
  } else {
    console.log(`[${timestamp}] [Sidecar Debug] ❌ Invoice NOT found with ID: ${idToUse}`);
  }

  return invoice ?? undefined;
});

const _currentTemplateDocument = computed(() => {
  const invoice = currentInvoiceForDebug.value;

  if (!invoice) return undefined;

  // 1️⃣ Check for explicit documentId field (future-proof)
  let documentId = (invoice as any)?.documentId;
  const mimeType = (invoice as any)?.mimeType ?? 'application/pdf';
  const filename = (invoice as any)?.name ?? invoice?.issuerLegalName ?? 'Invoice';

  // 2️⃣ For KI Suggestions: Try to use the invoice ID as document reference
  // KI suggestions might have a PDF stored with their ID
  if (!documentId && syncedTemplateSourceInvoice.value) {
    const isFromAi = aiSuggestions.value.some(
      (inv) => inv.id === syncedTemplateSourceInvoice.value?.id,
    );

    if (isFromAi) {
      // Use the invoice ID as the document ID for KI suggestions
      documentId = syncedTemplateSourceInvoice.value.id;
      console.log(`[PDF Viewer] Using KI suggestion ID as documentId: ${documentId}`);
    }
  }

  console.log(
    `[PDF Viewer] documentId: ${documentId}, mimeType: ${mimeType}, filename: ${filename}`,
  );

  if (!documentId) {
    console.log(`[PDF Viewer] No documentId found for invoice`);
    return undefined;
  }

  return {
    uuid: documentId,
    mime: mimeType,
    filename: filename,
  };
});

/**
 * 📎 Documents für KI-Suggestion aus props.ticket.documents
 * Zeige Dokumente wenn:
 * - Keine Nebenkostenabrechnung ausgewählt ist ODER
 * - KI-Suggestion aktiv ist (vom Sidecar synced)
 */
const _ticketDocumentsForKiSuggestion = computed(() => {
  // Show documents in sidecar when AI is active (synced value)
  const shouldShow = isUsingAiAsTemplateFromSync.value;

  if (!shouldShow) {
    console.log(
      `[Ticket Documents] Hidden: Contract selected (${currentContractId.value}) and not using AI (${isUsingAiAsTemplate.value})`,
    );
    return [];
  }

  // Hole Dokumente aus Ticket
  if (!props.ticket?.documents || !Array.isArray(props.ticket.documents)) {
    console.log(`[Ticket Documents] No documents in ticket`);
    return [];
  }

  console.log(
    `[Ticket Documents] Found ${props.ticket.documents.length} documents (showing because: ${!currentContractId.value ? 'no contract selected' : 'AI is active'})`,
  );
  return props.ticket.documents;
});

/** Helper: Format ISO date to "Jahr Monat" (e.g., "2026 März") based on current locale */
function formatIsoDateToYearMonth(isoDate: string | null | undefined): string {
  if (!isoDate) return 'No Period';

  try {
    // Parse the ISO date string
    const date = new Date(isoDate);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`[formatIsoDateToYearMonth] Invalid date: ${isoDate}`);
      return 'Invalid Date';
    }

    // Determine the locale to use
    const currentLocale = locale.value || 'en-US';

    // Use Intl.DateTimeFormat to get localized month name
    const formatter = new Intl.DateTimeFormat(currentLocale, {
      month: 'long',
      year: 'numeric',
    });

    const formatted = formatter.format(date);
    // Reorder to "Year Month" format
    const parts = formatted.split(' ');
    if (parts.length === 2) {
      return `${parts[1]} ${parts[0]}`;
    }
    return formatted;
  } catch (error) {
    console.error(`[formatIsoDateToYearMonth] Error formatting date: ${isoDate}`, error);
    return 'Invalid Date';
  }
}

/** Helper: Extract year from ISO date for sorting */
function extractYearFromIsoDate(isoDate: string | null | undefined): number {
  if (!isoDate) return 0;

  try {
    const date = new Date(isoDate);
    return isNaN(date.getTime()) ? 0 : date.getFullYear();
  } catch {
    return 0;
  }
}

/** Helper: Get IDs of invoices already linked to current ticket */
const invoiceIdsAlreadyInTicket = computed<Set<string>>(() => {
  if (!props.ticket?.documents) return new Set();

  const invoiceTypes = ['OPERATING_COST_INVOICE', 'OPERATING_COST_CORRECTION'];

  return new Set(
    props.ticket.documents
      .filter((doc) => invoiceTypes.includes(doc.documentType || ''))
      .map((doc) => doc.documentId || doc.id)
      .filter(Boolean),
  );
});

/** Dropdown options for template source selection */
const templateSourceOptions = computed(() => {
  const options: Array<{
    label: string;
    value: string;
    sourceType: InvoiceItemSuggestionSource;
  }> = [];

  // AI Suggestions option (first, highest priority)
  if (aiSuggestions.value.length > 0) {
    aiSuggestions.value.forEach((ai, _idx) => {
      options.push({
        label: `${t('pages.operatingcostinvoices.aiSuggestions')}`,
        value: ai.id,
        sourceType: 'ai',
      });
    });
  }

  // Latest Invoice option
  if (latestInvoice.value?.id) {
    const latestYear = extractYearFromIsoDate(latestInvoice.value.periodStart);
    const yearLabel = latestYear > 0 ? `${latestYear}` : '';
    options.push({
      label: `${t('pages.operatingcostinvoices.latestUtilitybill')} (${yearLabel}${yearLabel ? ': ' : ''}${latestInvoice.value.issuerLegalName || 'Unnamed'})`,
      value: latestInvoice.value.id,
      sourceType: 'latest',
    });
  }

  // Other existing invoices (sorted by year descending)
  // Filter out invoices that are already linked to this ticket
  const otherInvoices = invoices.value
    .filter((invoice) => !options.some((opt) => opt.value === invoice.id))
    .filter((invoice) => !invoiceIdsAlreadyInTicket.value.has(invoice.id))
    .sort((a, b) => {
      const yearA = extractYearFromIsoDate(a.periodStart);
      const yearB = extractYearFromIsoDate(b.periodStart);
      return yearB - yearA; // Descending (newest first)
    });

  otherInvoices.forEach((invoice) => {
    const year = extractYearFromIsoDate(invoice.periodStart);
    const formattedPeriod = formatIsoDateToYearMonth(invoice.periodStart);
    // Extract month from formatted period (format is "YYYY Monat")
    const month = formattedPeriod.split(' ').slice(1).join(' ');
    const yearLabel = year > 0 ? `${year}` : '';
    const label = yearLabel
      ? `${yearLabel} ${month}: ${invoice.issuerLegalName || 'Unnamed'}`
      : `${formattedPeriod}: ${invoice.issuerLegalName || 'Unnamed'}`;
    options.push({
      label: label,
      value: invoice.id,
      sourceType: 'other',
    });
  });

  return options;
});

/** Formatted version of templateSourceOptions with HTML bold tags for UI display */
const templateSourceOptionsFormatted = computed(() => {
  return templateSourceOptions.value.map((option) => {
    let formattedLabel = option.label;

    if (option.sourceType === 'ai') {
      // Bold the title for AI suggestions
      formattedLabel = `<strong>${option.label}</strong>`;
    } else if (option.sourceType === 'latest') {
      // Bold the title for Latest Invoice
      const match = option.label.match(/^(.+?)\s*\((.+?)\)$/);
      if (match) {
        const title = match[1];
        const rest = match[2];
        // Bold the title and year
        const yearMatch = rest.match(/^(\d{4})(.*)$/);
        if (yearMatch) {
          formattedLabel = `<strong>${title}</strong> (<strong>${yearMatch[1]}</strong>${yearMatch[2]})`;
        } else {
          formattedLabel = `<strong>${title}</strong> (${rest})`;
        }
      }
    } else if (option.sourceType === 'other') {
      // Bold the year for other invoices
      const match = option.label.match(/^(\d{4})\s+(.+?):\s+(.+)$/);
      if (match) {
        const year = match[1];
        const month = match[2];
        const name = match[3];
        formattedLabel = `<strong>${year}</strong> ${month}: ${name}`;
      }
    }

    return {
      ...option,
      label: formattedLabel,
    };
  });
});

/** Get the currently selected template source option with all properties */
const selectedTemplateSourceOption = computed(() => {
  if (!templateSourceInvoiceId.value) return undefined;
  return templateSourceOptions.value.find((opt) => opt.value === templateSourceInvoiceId.value);
});

// 📡 SYNC templateSourceOptionsFormatted to Sidecar (so Sidecar Select can be populated with formatted values)
// IMPORTANT: This watch MUST come after templateSourceOptionsFormatted is defined
watch(
  () => templateSourceOptionsFormatted.value,
  (options) => {
    // console.log(
    //   `[📡 SYNC] templateSourceOptionsFormatted changed. Count: ${options.length}, syncing to Sidecar`,
    // );
    syncState('templateSourceOptions', options);
  },
);

/** Determine the source of the current template ('ai', 'latest', or undefined) */
const templateSource = computed<'ai' | 'latest' | undefined>(() => {
  if (!templateSourceInvoiceId.value) return undefined;

  // Check if it's from AI suggestions
  const isFromAi = aiSuggestions.value.some((inv) => inv.id === templateSourceInvoiceId.value);
  if (isFromAi) return 'ai';

  // Check if it's the latest invoice
  if (latestInvoice.value?.id === templateSourceInvoiceId.value) return 'latest';

  // Otherwise it's from another existing invoice
  return undefined;
});

/** Dynamic "Compare with" tab label based on selected template */
const dynamicCompareTabLabel = computed<string>(() => {
  // No template selected
  if (!templateSourceInvoiceId.value || !selectedTemplateSourceOption.value) {
    return (
      t('pages.operatingcostinvoices.contentTabs.compareWithNoTemplate') ??
      'Mit Datenquelle vergleichen'
    );
  }

  // Get template source type to customize label
  const sourceType = selectedTemplateSourceOption.value?.sourceType;
  const templateName = selectedTemplateSourceOption.value?.label;

  switch (sourceType) {
    case 'ai':
      return t('pages.operatingcostinvoices.contentTabs.compareWithAi');
    case 'latest':
      return t('pages.operatingcostinvoices.contentTabs.compareWithLatest');
    case 'other':
      return t('pages.operatingcostinvoices.contentTabs.compareWithInvoice', {
        name: templateName,
      });
    default:
      return t('pages.operatingcostinvoices.contentTabs.compareWithNoTemplate');
  }
});

/** Dynamic tooltip for the compare tab */
const dynamicCompareTabTooltip = computed<string>(() => {
  if (!templateSourceInvoiceId.value || !selectedTemplateSourceOption.value) {
    return t('titletags.compareWithNoTemplate') ?? 'Wählen Sie eine Datenquelle zum Vergleichen';
  }

  const templateName = selectedTemplateSourceOption.value?.label;
  return (
    t('titletags.compareWithInvoice', { name: templateName }) ??
    `Vergleichen Sie die aktuelle Abrechnung mit: ${templateName}`
  );
});

// ----- SIDECAR SYNCED HEADER COMPUTEDS (based on synced values) --------

const syncedSelectedTemplateSourceOption = computed(() => {
  if (!syncedTemplateSourceId.value) return undefined;
  return sidecarTemplateSourceOptions.value.find(
    (opt) => opt.value === syncedTemplateSourceId.value,
  );
});

const dynamicCompareTabLabelForSidecar = computed<string>(() => {
  // No template selected
  if (!syncedTemplateSourceId.value || !syncedSelectedTemplateSourceOption.value) {
    return (
      t('pages.operatingcostinvoices.contentTabs.compareWithNoTemplate') ??
      'Mit Datenquelle vergleichen'
    );
  }

  // Get template source type to customize label
  const sourceType = syncedSelectedTemplateSourceOption.value?.sourceType;
  const templateName = syncedSelectedTemplateSourceOption.value?.label;

  switch (sourceType) {
    case 'ai':
      return (
        t('pages.operatingcostinvoices.contentTabs.compareWithAi') ?? 'Vergleich mit KI-Extraktion'
      );
    case 'latest':
      return (
        t('pages.operatingcostinvoices.contentTabs.compareWithLatest') ??
        'Vergleich mit letzter Abrechnung'
      );
    case 'other':
      return (
        t('pages.operatingcostinvoices.contentTabs.compareWithInvoice', { name: templateName }) ??
        `Vergleich mit ${templateName}`
      );
    default:
      return (
        t('pages.operatingcostinvoices.contentTabs.compareWithNoTemplate') ??
        'Mit Datenquelle vergleichen'
      );
  }
});

const dynamicCompareTabTooltipForSidecar = computed<string>(() => {
  if (!syncedTemplateSourceId.value || !syncedSelectedTemplateSourceOption.value) {
    return t('titletags.compareWithNoTemplate') ?? 'Wählen Sie eine Datenquelle zum Vergleichen';
  }

  const templateName = syncedSelectedTemplateSourceOption.value?.label;
  return (
    t('titletags.compareWithInvoice', { name: templateName }) ??
    `Vergleichen Sie die aktuelle Abrechnung mit: ${templateName}`
  );
});

const _isUsingLatestInvoiceAsTemplateFromSync = computed(() => {
  const templateId = syncedTemplateSourceId.value;
  const latestId = syncedLatestInvoiceId.value; // ← FIX: Use synced ID, not local latestInvoice
  return !!templateId && !!latestId && templateId === latestId;
});

// ----- MODE (isNew / editMode) ----------------------------------------------

const wrapperIsNew = computed(() => props.isNew ?? effectiveIsNew.value);

// Step 1 (master data) can be provided externally via prop (process slip)
// and falls back to local wrapper state.
const isStep1Successful = computed<boolean>(() => {
  if (props.step1Successful !== undefined) {
    return props.step1Successful;
  }

  return !!selectedInvoiceId.value;
});

/** Create mode if no invoice is selected (can be overridden by props.isNew) */
const effectiveIsNew = computed<boolean>(() => {
  // 1) explicit override from outside always wins
  if (props.isNew !== undefined) {
    return props.isNew;
  }

  // 2) fallback: derive from selectedInvoiceId
  return !selectedInvoiceId.value;
});

/** Effective editMode for detail component */
const effectiveEditMode = computed<boolean>(() => {
  // 1) explicit override from outside
  if (props.editMode !== undefined) {
    return props.editMode;
  }

  // 2) default: new invoices start in edit mode
  if (effectiveIsNew.value) {
    return true;
  }

  // 3) default for existing invoices
  return false;
});

// keep selectedInvoiceId in sync with external prop changes
watch(
  () => props.invoiceId,
  (val) => {
    selectedInvoiceId.value = val ?? undefined;
  },
);

/**
 * Auto-navigate to Panel 2 when Basisdaten are saved (in 'both' mode)
 * This happens after successful Panel 1 submission
 */
watch(
  () => selectedInvoiceId.value,
  () => {
    // Only auto-navigate if:
    // 1. We just saved data (selectedInvoiceId is now set)
    // 2. We're not in 'first' phase only mode
    // 3. We're currently on Panel 1
    if (selectedInvoiceId.value && props.phase !== 'first' && activeStepValue.value === 1) {
      // Auto-navigate to Panel 2 after Panel 1 is saved
      activeStepValue.value = 2;
      // Fetch LineItems for Panel 2
      void fetchLineItems();
    }
  },
);

/**
 * When loading an existing invoice (Phase 2),
 * extract the contract ID from the invoice and set it
 * so that detail lookups (contracts, pointOfSales) can load properly
 */
watch(selectedInvoiceId, async (invoiceId) => {
  // Only auto-load contract if:
  // 1. We have an invoiceId
  // 2. No external contractId prop is set (we're in loading mode)
  // 3. currentContractId is not already set
  if (!invoiceId || props.contractId || currentContractId.value) {
    return;
  }

  try {
    const invoiceDto = await fetchOperatingCostInvoice(invoiceId);
    if (invoiceDto?.contract?.id) {
      // Extract contract ID from the loaded invoice
      selectedContractIdFromDetail.value = invoiceDto.contract.id;
    }
  } catch (error) {
    console.error(
      '[OperatingCostInvoiceWrapper] Failed to load invoice for contract extraction:',
      invoiceId,
      error,
    );
  }
});

// Load AI suggestions when ticket's Nebenkostenabrechnung document is available
watch(
  [() => props.ticket?.id ?? null, () => ticketNebenkostenAbrechungDocumentId.value],
  async ([ticketId, documentId]) => {
    if (!props.hasAiFeature || !ticketId || !documentId) {
      aiSuggestions.value = [];
      isAiSourceActive.value = false;
      latestInvoice.value = undefined;
      templateSourceInvoiceId.value = undefined;
      return;
    }

    try {
      const suggestions = await getAiSuggestionsForDocument(ticketId, documentId);
      aiSuggestions.value = suggestions;
      // Auto-activate AI source if suggestions are available
      isAiSourceActive.value = suggestions.length > 0;
    } catch (error) {
      console.error('[OperatingCostInvoiceWrapper] Failed to load AI suggestions:', error);
      aiSuggestions.value = [];
      isAiSourceActive.value = false;
    }
  },
  { immediate: true },
);

// Auto-select first AI suggestion when they are loaded (priority over existing invoices + latest)
watch(aiSuggestions, (suggestions) => {
  // Only auto-select if:
  // 1. Suggestions are available
  // 2. No template is currently selected
  if (suggestions.length > 0 && !templateSourceInvoiceId.value) {
    const firstAiId = suggestions[0].id;
    const firstAiSuggestion = suggestions[0];

    // Auto-select AI suggestion (highest priority)
    templateSourceInvoiceId.value = firstAiId;
    selectedAiInvoiceRows.value = [firstAiSuggestion];
    refreshTemplateFromInvoice(firstAiSuggestion);

    // console.log('[🤖 Auto-Select AI] First AI suggestion selected:', firstAiId);

    // 📡 Sync AI suggestion to Sidecar
    syncState('templateSourceInvoice', firstAiSuggestion);
    syncState('templateSourceInvoiceId', firstAiId);
    syncState('isUsingAiAsTemplate', true);
  }
});

/**
 * 🎯 CRITICAL: Phase 2 auto-load
 * When starting directly in Phase 2 (existingInvoiceMode)
 * we need to fetch the line items immediately
 */
watch(
  () => ({ invoiceId: selectedInvoiceId.value, phase: props.phase, step: activeStepValue.value }),
  async ({ invoiceId, phase, step }) => {
    // If we have an invoice ID and we're in Phase 2 context, load line items
    if (invoiceId && (phase === 'second' || step === 2)) {
      console.log('[🎯 Phase 2 Auto-Load] Fetching line items for:', invoiceId);
      await fetchLineItems();
    }
  },
  { immediate: true, deep: false },
);

// suggestions over all invoices of this contract
const suggestions = computed(() =>
  buildFieldSuggestions<OperatingCostInvoiceSimple>(invoices.value, {
    includeFields: [
      'issuerLegalName',
      'vatRate',
      'customerPrepaymentNet',
      'customerPrepaymentGross',
      'statedCustomerPrepaymentNet',
      'settlementResultNet',
      'settlementResultGross',
    ],
  }),
);

// initialData for "create new invoice"
const initialDataForCreate = computed<Partial<OperatingCostInvoiceSimple>>(() => {
  // base values from internal template logic (latestValues)
  const base: Partial<OperatingCostInvoiceSimple> = {
    ...latestValues.value,
    contractId: contractSimple.value?.id ?? props.contractId,
    // Always ensure currency has a default value (EUR) for Intl.NumberFormat safety
    currency:
      (latestValues.value?.currency || props.initialDataOverride?.currency) ??
      OperatingCostInvoiceV1Dto.currency.EUR,
  };

  // no overrides → just use base
  if (!props.initialDataOverride) {
    return base;
  }

  // merge override on top of base (override wins)
  return {
    ...base,
    ...props.initialDataOverride,
  };
});

const placeholdersForDetail = computed<Partial<Record<keyof OperatingCostInvoiceSimple, string>>>(
  () => {
    const base = autoPlaceholders.value;

    if (!props.placeholdersOverride) {
      return base;
    }

    return {
      ...base,
      ...props.placeholdersOverride,
    };
  },
);

const latestInvoiceIdForDetail = computed(() => {
  return props.latestInvoiceIdOverride ?? latestInvoice.value?.id ?? undefined;
});

const latestInvoiceLineItemsForDetail = computed<OperatingCostInvoiceLineItemSimple[]>(() => {
  return props.latestInvoiceLineItemsOverride ?? latestInvoiceLineItems.value;
});

const disabledFieldsForDetail = computed<
  Partial<Record<keyof OperatingCostInvoiceSimple, boolean>>
>(() => {
  return (
    props.disabledFieldsOverride ??
    {
      // previous behavior, e.g., contractId locked etc.
    }
  );
});

// ----- HELPERS ----------------------------------------------------------------

/** Get icon and color classes for template source type */
function getSourceTypeIconClass(sourceType: InvoiceItemSuggestionSource): string {
  switch (sourceType) {
    case 'ai':
      return 'pi pi-sparkles text-purple-500';
    case 'latest':
      return 'pi pi-receipt text-blue-500';
    case 'other':
      return 'pi pi-receipt text-gray-500';
    default:
      return 'pi pi-receipt';
  }
}

function pickLatestInvoice(
  list: OperatingCostInvoiceSimple[],
): OperatingCostInvoiceSimple | undefined {
  if (!Array.isArray(list) || list.length === 0) return undefined;

  return (
    [...list].sort((a, b) => {
      return Date.parse(String(b.periodStart)) - Date.parse(String(a.periodStart));
    })[0] ?? undefined
  );
}

function isTemplateSourceIdAvailable(id: string | undefined): boolean {
  if (!id) return false;
  const inInvoices = invoices.value.some((inv) => inv.id === id);
  const inAiSuggestions = aiSuggestions.value.some((ai) => ai.id === id);
  return inInvoices || inAiSuggestions;
}

async function fetchLatestInvoiceWithLines() {
  if (!latestInvoice.value?.id) {
    latestInvoiceLineItems.value = [];
    latestInvoice.value = undefined;
    return;
  }

  const data = await fetchOperatingCostInvoice(String(latestInvoice.value.id));
  latestInvoiceLineItems.value =
    convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList(
      data.lineItems ?? [],
    );
}

function refreshTemplateFromInvoice(invoice: OperatingCostInvoiceSimple | undefined) {
  if (!invoice) {
    autoPlaceholders.value = {};
    latestValues.value = {};
    return;
  }

  const allPlaceholderData = {
    ...toRaw(invoice),
  };

  const { id: _id, version: _version, ...placeholderData } = allPlaceholderData;
  const isAiTemplateSource = aiSuggestions.value.some((ai) => ai.id === invoice.id);
  const hasNonEmptyString = (value: unknown): value is string =>
    typeof value === 'string' && value.trim() !== '';

  // For AI templates, periodEnd can be missing. In that case we fall back to the latest
  // existing invoice to keep a meaningful legacy period suggestion.
  const periodEndSeed = hasNonEmptyString(placeholderData.periodEnd)
    ? placeholderData.periodEnd
    : latestInvoice.value?.periodEnd;

  // 1) Determine next billing period
  const { nextStart, nextEnd } = getNextPeriod(periodEndSeed);

  // 2) Values we actually want to use as "suggestion for new billing"
  const nextDocumentReviewDeadline =
    getSameDayNextYear(String(placeholderData.documentReviewDeadline)) ?? undefined;
  const nextInternalObjectionDeadline =
    getSameDayNextYear(String(placeholderData.internalObjectionDeadline)) ?? undefined;
  const nextObjectionDeadline =
    getSameDayNextYear(String(placeholderData.objectionDeadline)) ?? undefined;

  const suggestedPeriodStart =
    isAiTemplateSource && hasNonEmptyString(placeholderData.periodStart)
      ? placeholderData.periodStart
      : nextStart;
  const suggestedPeriodEnd =
    isAiTemplateSource && hasNonEmptyString(placeholderData.periodEnd)
      ? placeholderData.periodEnd
      : nextEnd;

  // 3) Prefill object for initialData (will be written to the form)
  // NOTE: periodStart/periodEnd are intentionally NOT prefilled.
  // They should always be proposed via placeholders and applied by explicit user action.
  latestValues.value = {
    vatRate: placeholderData.vatRate ?? 19,
    // documentReviewDeadline: nextDocumentReviewDeadline,
    // internalObjectionDeadline: nextInternalObjectionDeadline,
    // objectionDeadline: nextObjectionDeadline,
    isHeatingCostsCompliantHKVO: placeholderData.isHeatingCostsCompliantHKVO === true, // Explicit boolean check
    contractId: placeholderData.contractId,
  };

  // 4) Placeholders
  const basePlaceholders = buildPlaceholders(placeholderData);

  autoPlaceholders.value = {
    ...basePlaceholders,
    // Dates: keep placeholder-only behavior for period fields.
    // Prefer AI suggestions when present, fallback to legacy next-period suggestion.
    issueDate: isAiTemplateSource
      ? basePlaceholders.issueDate
      : (latestValues.value.issueDate ?? basePlaceholders.issueDate),
    periodStart: suggestedPeriodStart ?? basePlaceholders.periodStart,
    periodEnd: suggestedPeriodEnd ?? basePlaceholders.periodEnd,
    documentReviewDeadline: isAiTemplateSource
      ? basePlaceholders.documentReviewDeadline
      : (latestValues.value.documentReviewDeadline ?? nextDocumentReviewDeadline),
    internalObjectionDeadline: isAiTemplateSource
      ? basePlaceholders.internalObjectionDeadline
      : (latestValues.value.internalObjectionDeadline ?? nextInternalObjectionDeadline),
    objectionDeadline: isAiTemplateSource
      ? basePlaceholders.objectionDeadline
      : (latestValues.value.objectionDeadline ?? nextObjectionDeadline),
  };
}

// ----- HANDLER ----------------------------------------------------------------

function openInvoicePreview(id: string) {
  // open detail in modal
  showInvoiceRef.value?.openInModal({ uuid: id });
}

/**
 * Handle step change attempts in the Stepper
 * Prevents jumping to Panel 2 if Basisdaten are not yet saved
 */
async function onStepChange(nextStep: number) {
  // In 'first' phase, only panel 1 is allowed.
  if (props.phase === 'first' && nextStep !== 1) {
    activeStepValue.value = 1;
    return;
  }

  // If trying to navigate to Panel 2 but it's not accessible, prevent it
  if (nextStep === 2 && !isPanel2Accessible.value) {
    console.warn('[Stepper Guard] Cannot jump to Panel 2 - Basisdaten not yet saved');
    activeStepValue.value = 1; // Force back to Panel 1
    return;
  }

  // 🎯 CRITICAL: Wait for Vue to update DOM before rendering panel content
  // Prevents race condition where panel appears empty on first click back to Panel 1
  await nextTick();
}

/**
 * Fetch LineItems for the currently selected Invoice (Step 2)
 */
async function fetchLineItems() {
  if (!selectedInvoiceId.value) {
    editDataLineItems.value = [];
    currentInvoiceContractId.value = undefined;
    return;
  }

  const invoiceDto = await fetchOperatingCostInvoice(selectedInvoiceId.value);
  editDataLineItems.value =
    convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList(
      invoiceDto.lineItems ?? [],
    );
  currentInvoiceContractId.value = invoiceDto.contract?.id ?? undefined;
}

/**
 * Watcher: Reagiert auf JEDEN Wechsel von templateSourceInvoiceId
 * - Wenn Select geändert wird (onTemplateSourceSelected)
 * - Wenn Tabellenzeile ausgewählt wird (onRowClick)
 *
 * Aktualisiert Template und synced zu anderen Panels/ExternalWindow
 */
watch(
  () => templateSourceInvoiceId.value,
  (newId) => {
    if (!newId) {
      return;
    }

    const selectedInvoice =
      invoices.value.find((inv) => inv.id === newId) ||
      aiSuggestions.value.find((inv) => inv.id === newId);

    if (!selectedInvoice) {
      return;
    }

    // Update template from selected invoice
    refreshTemplateFromInvoice(selectedInvoice);

    // 📡 Sync to all contexts (local + external window) via adapter
    // Send the complete invoice object, not just the ID!
    // This way the Sidecar has all the data without needing local invoices array
    syncState('templateSourceInvoice', selectedInvoice);
    syncState('templateSourceInvoiceId', newId);
    syncState('isUsingAiAsTemplate', isUsingAiAsTemplate.value);
  },
  { immediate: false },
);

/**
 * Watcher: When isUsingAiAsTemplate changes, sync it to Sidecar
 * This is critical for the Sidecar to decide which content tabs to display
 */
watch(
  () => isUsingAiAsTemplate.value,
  (newValue) => {
    syncState('isUsingAiAsTemplate', newValue);
  },
);

/**
 * Watcher: Sync templateSourceOptions to Sidecar
 * Sidecar Select needs the same options as Main Panel
 */
watch(
  () => templateSourceOptions.value,
  (newOptions) => {
    syncState('templateSourceOptions', newOptions);
  },
);

/**
 * Watcher: Sync latestInvoiceId to Sidecar
 * CRITICAL: Sidecar needs this to compare latest vs selected template
 */
watch(
  () => latestInvoice.value?.id,
  (newLatestId) => {
    if (newLatestId) {
      syncState('latestInvoiceId', newLatestId);
    }
  },
);

/**
 * Watcher: Sync currentContractId to Sidecar
 * CRITICAL: This lets the Sidecar know which contract is selected in Main
 */
watch(
  () => currentContractId.value,
  (newContractId) => {
    syncState('currentContractId', newContractId);
  },
);

function onRowClick(event: DataTableRowClickEvent) {
  const rawId = event?.data?.id;
  const id = typeof rawId === 'string' ? rawId : rawId?.meta ? rawId.meta('rawvalue') : undefined;

  if (!id) return;

  // use clicked invoice as new template source
  templateSourceInvoiceId.value = id;
  const inv = invoices.value.find((i) => i.id === id) ?? undefined;
  refreshTemplateFromInvoice(inv);

  // Highlight the selected row
  selectedExistingInvoiceRows.value = [event.data];

  // optional: still open preview modal
  if (props.existingListMode === 'modal') {
    openInvoicePreview(id);
  }

  // DO NOT set selectedInvoiceId here – we stay in "new invoice" mode
}

function onCreated(payload?: { id?: string }) {
  selectedInvoiceId.value = payload?.id ?? undefined;
  emit('created', { id: payload?.id });
}

/**
 * Handle when an existing invoice is loaded from the Detail component
 * This happens when starting in Phase 2 (existing invoice mode)
 *
 * Wichtig: Wir müssen hier auch AI-Suggestions laden, damit sie
 * als "ai" source gekennzeichnet werden. Sonst werden sie als "actual" markiert.
 */
async function onInvoiceLoaded(invoice: OperatingCostInvoiceSimple) {
  console.log('[🎯 onInvoiceLoaded] Invoice loaded:', invoice);
  console.log(
    '[🎯 onInvoiceLoaded] Invoice has lineItems?',
    !!invoice.lineItems,
    'Count:',
    invoice.lineItems?.length ?? 0,
  );

  // 1️⃣ Set the ACTUAL line items from the loaded invoice
  if (invoice.lineItems && invoice.lineItems.length > 0) {
    editDataLineItems.value = invoice.lineItems;
    console.log('[🎯 onInvoiceLoaded] Actual line items set:', editDataLineItems.value.length);
  } else {
    console.log('[🎯 onInvoiceLoaded] ⚠️ No lineItems in loaded invoice!');
    editDataLineItems.value = [];
  }

  // 2️⃣ Load AI Suggestions for this invoice
  // These show the AI data extraction, so they are not marked as "actual"
  if (!props.hasAiFeature) {
    return;
  }

  try {
    const ticketId = props.ticket?.id ?? null;
    const docInternalID = ticketNebenkostenAbrechungDocumentId.value ?? null;
    console.log(
      '[🎯 onInvoiceLoaded] Loading AI suggestions for ticket/doc:',
      ticketId,
      docInternalID,
    );

    const suggestions = await getAiSuggestionsForDocument(ticketId, docInternalID);
    console.log('[🎯 onInvoiceLoaded] Got suggestions:', suggestions);

    if (suggestions && suggestions.length > 0) {
      aiSuggestions.value = suggestions;
      isAiSourceActive.value = true;

      const firstSuggestion = suggestions[0];

      // 🎯 CRITICAL: Auto-select first AI suggestion (priority over latestInvoice in Phase 2)
      templateSourceInvoiceId.value = firstSuggestion.id;
      refreshTemplateFromInvoice(firstSuggestion);
      console.log('[🎯 onInvoiceLoaded] ✅ First AI suggestion auto-selected:', firstSuggestion.id);

      // Log AI lineItems from first suggestion
      console.log(
        '[🎯 onInvoiceLoaded] First AI suggestion has lineItems?',
        !!firstSuggestion.lineItems,
        'Count:',
        firstSuggestion.lineItems?.length ?? 0,
      );
      console.log('[🎯 onInvoiceLoaded] First AI lineItems:', firstSuggestion.lineItems);

      console.log(
        '[🎯 onInvoiceLoaded] aiSuggestionsLineItems computed will now return:',
        firstSuggestion.lineItems,
      );
    } else {
      console.log('[🎯 onInvoiceLoaded] No AI suggestions available');
    }
  } catch (error) {
    console.warn('[🎯 onInvoiceLoaded] Failed to load AI suggestions:', error);
  }
}

function onLineItemsCreated(payload: { ok: boolean }) {
  emit('lineItemsCreated', payload);
}

// ----- STATE SYNC (Cross-Panel Communication) ----------------------------------

const addDebugMessage = (key: string, value: any, type: string = 'event') => {
  const timestamp = new Date().toLocaleTimeString();
  debugSidecarMessages.value.unshift({
    timestamp,
    key,
    value,
    type,
  });

  // Keep only last 5 messages
  if (debugSidecarMessages.value.length > 5) {
    debugSidecarMessages.value.pop();
  }
};

// 🔔 Notifications für State-Changes (Sidecar + ExternalWindow)
// Context: 'sidecar' means we are in the Sidecar panel and listen to all state changes
useStateSyncNotifications({
  context: 'sidecar',
  onStateChange: (payload, showNotification) => {
    // Handle templateSourceOptions sync
    if (payload.key === 'templateSourceOptions') {
      sidecarTemplateSourceOptions.value = payload.value || [];
      return;
    }

    // Update with complete invoice object if available
    if (payload.key === 'templateSourceInvoice') {
      syncedTemplateSourceInvoice.value = payload.value as OperatingCostInvoiceSimple;
      syncedTemplateSourceId.value = payload.value?.id ?? null;

      const message = `Template updated to: ${payload.value?.issuerLegalName ?? 'Unknown'}`;
      showNotification(message);
      return;
    }

    // Fallback: if only ID is sent
    if (payload.key === 'templateSourceInvoiceId') {
      syncedTemplateSourceId.value = payload.value;

      // Show custom message with template details
      const message = `Template updated to: ${getSyncedTemplateLabel()}`;
      showNotification(message);
      return;
    }

    // Handle isUsingAiAsTemplate sync
    if (payload.key === 'isUsingAiAsTemplate') {
      isUsingAiAsTemplateFromSync.value = payload.value;

      const message = payload.value ? '🤖 KI-Suggestion ist aktiv' : '📄 KI-Suggestion deaktiviert';
      showNotification(message);
      return;
    }

    // Handle latestInvoiceId sync
    if (payload.key === 'latestInvoiceId') {
      syncedLatestInvoiceId.value = payload.value;
      return;
    }

    // Handle currentContractId sync
    if (payload.key === 'currentContractId') {
      syncedCurrentContractId.value = payload.value;

      const message = payload.value ? `✅ Vertrag ausgewählt` : '❌ Kein Vertrag ausgewählt';
      showNotification(message);
      return;
    }
  },
});

// 🔔 Notifications for State Changes (Main Panel Context)
// Context: 'mainPanel' means we are in the Main Panel and listen to changes FROM THE SIDECAR
useStateSyncNotifications({
  context: 'mainPanel',
  onStateChange: (payload, showNotification) => {
    // Ignore own updates (sender ist 'mainPanel')
    if (payload.sender === 'mainPanel') {
      return;
    }

    // Handle Sidecar sending back templateSourceInvoiceId
    if (payload.key === 'templateSourceInvoiceId' && payload.sender === 'sidecar') {
      templateSourceInvoiceId.value = payload.value;

      const message = `🔄 Template selection from Sidecar updated`;
      showNotification(message);
      return;
    }
  },
});

function getSyncedTemplateLabel(): string {
  // 1️⃣ PREFER: Use synced invoice object if available
  if (syncedTemplateSourceInvoice.value) {
    return syncedTemplateSourceInvoice.value.issuerLegalName || 'Unnamed';
  }

  // 2️⃣ FALLBACK: Use ID and search local arrays
  if (!syncedTemplateSourceId.value) return 'No Template';

  const fromAi = aiSuggestions.value.find((inv) => inv.id === syncedTemplateSourceId.value);
  if (fromAi) return `AI Suggestion (${fromAi.issuerLegalName || 'Unnamed'})`;

  const fromInvoice = invoices.value.find((inv) => inv.id === syncedTemplateSourceId.value);
  if (fromInvoice) return `${fromInvoice.issuerLegalName || 'Unnamed'}`;

  return 'Unknown Template';
}

// Clear AI selection when Existing Invoice is selected
watch(selectedExistingInvoiceRows, (newVal) => {
  if (newVal && newVal.length > 0) {
    selectedAiInvoiceRows.value = [];
  }
});

// Clear Existing Invoice selection when AI Suggestion is selected
watch(selectedAiInvoiceRows, (newVal) => {
  if (newVal && newVal.length > 0) {
    selectedExistingInvoiceRows.value = [];
  }
});

watch(
  invoices,
  async (list) => {
    const currentTemplateId = templateSourceInvoiceId.value;

    if (!list.length) {
      latestInvoice.value = undefined;
      latestInvoiceLineItems.value = [];
      autoPlaceholders.value = {};
      latestValues.value = {};

      // Preserve current selection (especially AI) if it is still available.
      if (!isTemplateSourceIdAvailable(currentTemplateId)) {
        templateSourceInvoiceId.value = undefined;
      }

      refreshTemplateFromInvoice(templateSourceInvoice.value);
      return;
    }

    // always compute true "latest"
    latestInvoice.value = pickLatestInvoice(list);

    // 📡 SYNC latestInvoiceId immediately to Sidecar (don't wait for watcher)
    if (latestInvoice.value?.id) {
      syncState('latestInvoiceId', latestInvoice.value.id);
    }

    await fetchLatestInvoiceWithLines();

    // Preserve current template when still valid; otherwise fall back to latest.
    if (!isTemplateSourceIdAvailable(templateSourceInvoiceId.value)) {
      templateSourceInvoiceId.value = latestInvoice.value?.id;
    }

    // if no template source chosen yet, default to latest invoice
    if (!templateSourceInvoiceId.value && latestInvoice.value?.id) {
      templateSourceInvoiceId.value = latestInvoice.value.id;
    }

    // build placeholders and initial values from current template source
    refreshTemplateFromInvoice(templateSourceInvoice.value);
  },
  { immediate: true },
);
</script>

<style scoped>
:deep(.p-steppanel) {
  background: transparent;
  border: none;
}

/* Optional: auch die inneren Wrapper */
:deep(.p-steppanel-content) {
  background: transparent;
}
</style>
